import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Users, Sparkles, ArrowUpRight, Quote, UserCheck } from "lucide-react";
import SectionHeader from "@/components/site/SectionHeader";

const TRUSTEES = [
  { name: "Shailesh Bagwe", role: "Founder & Chairperson", isChair: true, photo: "/founder.jpg" },
  { name: "Qing Wang", role: "Trustee", photo: "/qing.jpg" },
  { name: "Raj Singh", role: "Trustee", photo: "/raj.jpg" },
  { name: "Joseph Chen", role: "Trustee", photo: "/joseph.jpg" },
  { name: "Kerrin Leoni", role: "Trustee", photo: "/kerrin.jpg" },
];

export default function Leadership() {
  return (
    <>
      <section className="container-x pt-20 pb-16 sm:pt-28">
        <div className="max-w-4xl">
          <span className="label-eyebrow">— Leadership &amp; Governance</span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-7xl font-heading font-semibold tracking-tighter leading-[1.02] text-balance">
            Transparent, community-grounded governance.
          </h1>
          <p className="mt-8 text-lg text-ink/65 max-w-2xl leading-relaxed">
            Aoturoa Academy Charitable Trust (CC58012) was founded by Shailesh Bagwe, who also serves as Chairperson of
            the Board of Trustees. Our governance structure brings together community, education and public-health voices
            grounded in accountability and youth wellbeing.
          </p>
        </div>
      </section>

      {/* TRUST & GOVERNANCE (includes Founder profile) */}
      <section className="section-y !pt-8">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <ShieldCheck className="w-8 h-8 text-crimson mb-5" />
              <h2 className="text-3xl sm:text-4xl font-heading font-semibold leading-tight">Trust &amp; Governance</h2>
            </div>
            <div className="lg:col-span-7 text-ink/75 leading-relaxed space-y-5 text-lg">
              <p>
                Aoturoa Academy Charitable Trust is governed through a community-focused structure focused on youth
                wellbeing, accessibility, and evidence-informed education.
              </p>
              <ul className="space-y-3 text-base">
                <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 mt-2.5 rounded-full bg-crimson flex-shrink-0" /><span><strong className="text-ink font-medium">Board of Trustees</strong> — five trustees appointed in line with our charitable trust deed, chaired by the Founder.</span></li>
                <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 mt-2.5 rounded-full bg-crimson flex-shrink-0" /><span><strong className="text-ink font-medium">Governance structure</strong> — designed for transparency, accountability and community representation.</span></li>
                <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 mt-2.5 rounded-full bg-crimson flex-shrink-0" /><span><strong className="text-ink font-medium">Community-led development</strong> — decisions shaped in partnership with those we serve.</span></li>
              </ul>
              <p className="text-sm text-ink/55 pt-4 border-t border-stone">
                Registered Charitable Trust · CC58012 · Founded by Shailesh Bagwe
              </p>
            </div>
          </div>

          {/* FOUNDER PROFILE — nested within governance */}
          <div className="mt-16 bg-white border border-stone rounded-3xl overflow-hidden" data-testid="founder-profile">
            <div className="grid grid-cols-1 lg:grid-cols-12 items-center">
              <div className="lg:col-span-5 bg-sand p-8 sm:p-12 flex items-center justify-center">
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-crimson/8 scale-110 blur-2xl" aria-hidden="true" />
                  <div className="relative w-56 h-56 sm:w-72 sm:h-72 lg:w-80 lg:h-80 rounded-full overflow-hidden bg-white ring-1 ring-stone shadow-[0_20px_60px_rgba(0,0,0,0.08)]">
                    <img
                      src="/founder.jpg"
                      alt="Shailesh Bagwe, Founder & Chairperson of Aoturoa Academy Charitable Trust"
                      data-testid="founder-image"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-ink text-bone text-[10px] uppercase tracking-[0.2em] px-4 py-1.5 rounded-full font-semibold whitespace-nowrap">
                    Founder &amp; Chair
                  </div>
                </div>
              </div>
              <div className="lg:col-span-7 p-8 sm:p-12 lg:p-14 flex flex-col justify-center">
                <span className="label-eyebrow">— A message from our Founder</span>
                <h3 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-heading font-semibold tracking-tight leading-[1.05] text-ink" data-testid="founder-name">
                  Shailesh Bagwe
                </h3>
                <p className="mt-2 text-crimson font-medium">
                  Founder, Aoturoa Academy Charitable Trust<br />
                  <span className="text-ink/55 font-normal">Chairperson, Board of Trustees</span>
                </p>

                <Quote className="w-9 h-9 text-crimson mt-10 mb-5" />

                <div className="space-y-5 text-ink/80 leading-relaxed text-lg max-w-2xl">
                  <p className="text-xl font-heading text-ink leading-snug italic text-balance">
                    Through Aoturoa Academy Charitable Trust, I have had the privilege of supporting initiatives that
                    strengthen communities, empower young people, and create opportunities for positive change.
                  </p>
                  <p>
                    Our commitment has always been to education, wellbeing, and ensuring that individuals have access to
                    the support and knowledge they need to thrive. We believe strong communities are built through
                    connection, understanding, and a shared commitment to lifting others up.
                  </p>
                  <p>
                    Youth &amp; Community Sexual Health Promotion Aotearoa reflects these values by working toward
                    accessible, culturally responsive, and evidence-informed wellbeing education for rangatahi and
                    communities across Aotearoa.
                  </p>
                  <p>
                    As Founder of Aoturoa Academy Charitable Trust and Chairperson of the Board of Trustees, I am proud
                    to support initiatives that seek to make a meaningful and lasting difference in the lives of young
                    people and the communities they belong to.
                  </p>
                </div>

                <div className="mt-10 pt-8 border-t border-stone" data-testid="founder-signature">
                  <p className="font-heading text-2xl text-ink italic tracking-tight">Shailesh Bagwe</p>
                  <p className="mt-2 text-sm text-ink/65 leading-relaxed">
                    Founder, Aoturoa Academy Charitable Trust<br />
                    Chairperson, Board of Trustees
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* BOARD OF TRUSTEES */}
          <div className="mt-16" data-testid="trust-members">
            <div className="flex items-center justify-between gap-6 mb-8 flex-wrap">
              <div className="flex items-center gap-3">
                <UserCheck className="w-6 h-6 text-crimson" />
                <h3 className="text-2xl sm:text-3xl font-heading font-semibold leading-tight">Board of Trustees</h3>
              </div>
              <p className="text-sm text-ink/55 max-w-md">
                Our trustees bring together community, education, and public-health expertise to guide the kaupapa with
                accountability and integrity.
              </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              {TRUSTEES.map((t, i) => (
                <div
                  key={t.name}
                  className={`relative rounded-2xl p-6 transition-all hover:-translate-y-1 ${
                    t.isChair
                      ? "bg-ink text-bone border border-ink"
                      : "bg-white border border-stone hover:border-ink/30"
                  }`}
                  data-testid={`trustee-${i}`}
                >
                  <div className={`w-16 h-16 rounded-full overflow-hidden mb-5 ring-1 ${t.isChair ? "ring-white/15" : "ring-stone"}`}>
                    {t.photo ? (
                      <img src={t.photo} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className={`w-full h-full flex items-center justify-center ${t.isChair ? "bg-white/10" : "bg-sand"}`}>
                        <span className={`font-heading font-semibold text-lg ${t.isChair ? "text-bone/70" : "text-ink/50"}`}>
                          {t.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                        </span>
                      </div>
                    )}
                  </div>
                  <h4 className={`font-heading font-semibold leading-tight ${t.isChair ? "text-bone" : "text-ink"}`}>
                    {t.name}
                  </h4>
                  <p className={`mt-2 text-xs uppercase tracking-wider font-semibold ${t.isChair ? "text-crimson" : "text-crimson"}`}>
                    {t.role}
                  </p>
                  {t.isChair && (
                    <span className="absolute top-4 right-4 text-[9px] uppercase tracking-[0.18em] font-semibold bg-crimson text-white px-2 py-1 rounded-full">
                      Chair
                    </span>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-ink/55">
              Interested in supporting our governance or future advisory work?{" "}
              <Link to="/contact" data-testid="trustee-eoi-link" className="text-crimson underline-offset-4 hover:underline">Get in touch</Link>.
            </p>
          </div>
        </div>
      </section>
      <section className="bg-sand">
        <div className="container-x py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <Users className="w-8 h-8 text-crimson mb-5" />
              <h2 className="text-3xl sm:text-4xl font-heading font-semibold leading-tight">Steering Group</h2>
              <span className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-crimson bg-white border border-crimson/20 px-3 py-1.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-crimson animate-pulse" />
                Established · Welcoming new members
              </span>
            </div>
            <div className="lg:col-span-7 text-ink/75 leading-relaxed space-y-5 text-lg">
              <p>
                We have an established steering group that supports strategic planning, programme development, and
                community engagement — and we warmly welcome new members as the initiative continues to grow.
              </p>
              <ul className="space-y-3 text-base">
                <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 mt-2.5 rounded-full bg-crimson flex-shrink-0" /><span>Established core group guiding day-to-day kaupapa</span></li>
                <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 mt-2.5 rounded-full bg-crimson flex-shrink-0" /><span>Open to new members across health, education and community sectors</span></li>
                <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 mt-2.5 rounded-full bg-crimson flex-shrink-0" /><span>Youth wellbeing and education focus at the heart of every decision</span></li>
              </ul>
              <p className="pt-4 text-sm text-ink/55">
                Interested in joining the steering group?{" "}
                <Link to="/contact" className="text-crimson underline-offset-4 hover:underline">Get in touch</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PROJECT TEAM */}
      <section className="section-y">
        <div className="container-x">
          <div className="flex items-center gap-4 mb-10">
            <Sparkles className="w-8 h-8 text-crimson" />
            <h2 className="text-3xl sm:text-4xl font-heading font-semibold leading-tight">Project Team</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-5">
              <div className="bg-white border border-stone rounded-3xl overflow-hidden">
                <div className="bg-sand p-8 sm:p-10 flex items-center justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 rounded-full bg-crimson/8 scale-110 blur-2xl" aria-hidden="true" />
                    <div className="relative w-44 h-44 sm:w-52 sm:h-52 rounded-full overflow-hidden bg-white ring-1 ring-stone shadow-[0_20px_60px_rgba(0,0,0,0.08)]">
                      <img
                        src="/alex.jpg"
                        alt="Alex Stephanie Amboy, Project Lead & Lead Educator"
                        data-testid="lead-image"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-ink text-bone text-[10px] uppercase tracking-[0.2em] px-4 py-1.5 rounded-full font-semibold whitespace-nowrap">
                      Project Lead
                    </div>
                  </div>
                </div>
                <div className="p-8 sm:p-10 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-crimson/5 rounded-full -translate-y-1/3 translate-x-1/3" aria-hidden="true" />
                  <div className="relative">
                    <span className="label-eyebrow">— Project Lead</span>
                    <h3 className="mt-4 text-2xl font-heading font-semibold text-ink leading-tight" data-testid="lead-name">Alex Stephanie Amboy</h3>
                    <p className="mt-2 text-crimson font-medium">Project Lead &amp; Lead Educator</p>
                    <p className="mt-6 text-ink/70 leading-relaxed text-[15px]">
                      Alex Stephanie Amboy leads the development of the initiative, focusing on youth wellbeing, sexual
                      health literacy, community outreach, and education development across Auckland.
                    </p>
                    <a href="mailto:alex.amboy@aoturoaacademy.org.nz" data-testid="lead-email-link" className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-ink hover:text-crimson transition-colors break-all">
                      alex.amboy@aoturoaacademy.org.nz <ArrowUpRight className="w-4 h-4 flex-shrink-0" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="bg-ink text-bone rounded-3xl p-8 sm:p-10 h-full flex flex-col justify-between">
                <div>
                  <span className="label-eyebrow">— Growing the team</span>
                  <h3 className="mt-4 text-2xl font-heading font-semibold leading-tight">A small team with a wide kaupapa.</h3>
                  <p className="mt-6 text-bone/70 leading-relaxed">
                    We are an early-stage initiative and our team is intentionally lean. As the kaupapa grows, we expect to
                    welcome additional educators, advisors, and youth voices — always in step with the communities we serve.
                  </p>
                </div>
                <div className="mt-10 pt-8 border-t border-white/10 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-bone/45 text-xs uppercase tracking-wider">Status</div>
                    <div className="mt-1 font-heading text-bone">Emerging initiative</div>
                  </div>
                  <div>
                    <div className="text-bone/45 text-xs uppercase tracking-wider">Based</div>
                    <div className="mt-1 font-heading text-bone">Auckland, NZ</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-crimson text-white">
        <div className="container-x py-20 lg:py-24 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-semibold tracking-tight max-w-3xl mx-auto text-balance">
            Want to support the kaupapa or join the conversation?
          </h2>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link to="/contact" data-testid="leadership-connect-btn" className="bg-ink text-white px-7 py-3.5 rounded-full font-medium text-sm hover:bg-black hover:-translate-y-0.5 transition-all">
              Connect With Us
            </Link>
            <Link to="/partners" data-testid="leadership-collab-btn" className="border border-white text-white px-7 py-3.5 rounded-full font-medium text-sm hover:bg-white hover:text-crimson transition-all">
              Community Collaboration
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
